import type { ReactNode, ComponentType, PropsWithChildren } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';

import { useLocation } from '@xing-com/crate-core-router';
import { sanitizeUrl } from '@xing-com/crate-people-directory-utils';
import { IconHome } from '@xing-com/icons';

import {
  Container,
  StyledBodyCopy,
  StyledInternalLink,
  StyledExternalLink,
  StyledIconArrowDown,
  ArrowWrapper,
} from './breadcrumbs.styles';
import {
  XING_URL,
  PEOPLE_DIRECTORY_ROUTE,
  CITIES_LISTING_PAGE,
} from './routes';

export type Breadcrumb = {
  title: string | ReactNode;
  url: string;
  isExternalUrl?: boolean;
};

export type BreadcrumbsProps = {
  items?: Breadcrumb[];
};

const ICON_SIZE = 18;

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();
  const isCitiesRoute = pathname && pathname.includes('cities');

  const breadcrumbs: Breadcrumb[] = [
    {
      title: <IconHome height={ICON_SIZE} />,
      url: XING_URL,
      isExternalUrl: true,
    },
    {
      title: formatMessage({ id: 'PD_100_TITLE' }),
      url: PEOPLE_DIRECTORY_ROUTE,
    },
    ...(isCitiesRoute
      ? [
          {
            title: formatMessage({ id: 'PD_CITIES_PEOPLE_IN_CITIES_HEADLINE' }),
            url: CITIES_LISTING_PAGE,
          },
        ]
      : []),
    ...(items || []),
  ];

  return (
    <Container>
      {breadcrumbs.map(({ title, url, isExternalUrl }, index) => {
        let LinkComponent: ComponentType<PropsWithChildren<unknown>>;
        let linkParams = {};
        const isLastItem = index === breadcrumbs.length - 1;
        const formattedUrl = sanitizeUrl(url);

        if (!isExternalUrl) {
          LinkComponent = StyledInternalLink;
          linkParams = { to: formattedUrl };
        } else {
          LinkComponent = StyledExternalLink;
          linkParams = { href: formattedUrl };
        }

        return (
          <React.Fragment key={url + index}>
            <LinkComponent
              {...linkParams}
              aria-label={
                url === XING_URL
                  ? formatMessage({
                      id: 'XE_HOME_BREADCRUMB',
                    })
                  : undefined
              }
            >
              <StyledBodyCopy
                size="xsmall"
                fontWeight={isLastItem ? 'bold' : 'regular'}
                lineHeight="dense"
                noMargin
              >
                {title}
              </StyledBodyCopy>
            </LinkComponent>
            {!isLastItem && (
              <ArrowWrapper>
                <StyledIconArrowDown height={ICON_SIZE} />
              </ArrowWrapper>
            )}
          </React.Fragment>
        );
      })}
    </Container>
  );
};
